import React, { useCallback, useEffect, useMemo, useState } from "react";
import { t } from "../../../i18n";
import { AdminMfaProvider, AdminPersonalToken, PermissionCategory, TokenStatus } from "../../../http/protocol";
import { getContainer } from "../../../ioc/IOCSetup";
import { DeleteButtonModal } from "../../../util/DeleteButtonModal";
import { formatInstant } from "../../../util/date";
import { PermissionRequired } from "../../security/PermissionRequired";
import { MintTokenDialog } from "./MintTokenDialog";
import { httpErrorMessage } from "./httpErrorMessage";

interface Props {
  /** Passed to the mint dialog, which needs it to know whether a code has to be mailed first. */
  mfaProvider?: AdminMfaProvider;
}

interface TokenRowProps {
  token: AdminPersonalToken;
  onRevoke: (tokenId: number) => Promise<void>;
}

function statusLabel(status?: TokenStatus): string {
  switch (status) {
    case TokenStatus.ACTIVE:
      return t("api-tokens.status.active", undefined, "Active");
    case TokenStatus.EXPIRED:
      return t("api-tokens.status.expired", undefined, "Expired");
    case TokenStatus.REVOKED:
      return t("api-tokens.status.revoked", undefined, "Revoked");
    default:
      return t("api-tokens.status.unknown", undefined, "Unknown");
  }
}

function statusBadgeClass(status?: TokenStatus): string {
  return status === TokenStatus.ACTIVE ? "badge badge-success" : "badge badge-secondary";
}

function TokenRow({ token, onRevoke }: TokenRowProps): JSX.Element {
  const tokenId = token.id;
  return (
    <tr data-testid={`api-token-row-${tokenId}`}>
      <td data-testid={`api-token-label-${tokenId}`}>{token.label}</td>
      <td data-testid={`api-token-created-${tokenId}`}>{formatInstant(token.created)}</td>
      <td data-testid={`api-token-expiry-${tokenId}`}>{formatInstant(token.expiry)}</td>
      <td data-testid={`api-token-status-${tokenId}`}>
        <span className={statusBadgeClass(token.status)}>{statusLabel(token.status)}</span>
      </td>
      <td>
        {/* An expired or revoked token holds no slot and reaches nothing, so there is nothing to take away. */}
        {token.status === TokenStatus.ACTIVE && tokenId !== undefined && (
          // Revoking is a write on the server, while reading the list is not: a holder who may only
          // read their tokens is offered the audit view without a control the server would refuse.
          <PermissionRequired
            permissions={[PermissionCategory.MCP_ACCESS]}
            accessLevel="w"
          >
            <span data-testid={`api-token-revoke-${tokenId}`}>
              <DeleteButtonModal
                className="btn btn-danger btn-sm waves-effect waves-light"
                label={t("api-tokens.revoke", undefined, "Revoke")}
                description={t(
                  "api-tokens.revoke-description",
                  { label: token.label },
                  'the API token "{{label}}". Any client still using it stops working immediately.',
                )}
                onDelete={() => onRevoke(tokenId)}
              />
            </span>
          </PermissionRequired>
        )}
      </td>
    </tr>
  );
}

/**
 * Lists the admin's own personal tokens and owns their whole lifecycle on this page.
 *
 * The card loads its own tokens rather than being fed them by the profile page: the endpoint is
 * gated on a permission the rest of that page does not need, so a refused request here must not be
 * able to take the page's other cards down with it.
 */
export function ApiTokensCard({ mfaProvider }: Props): JSX.Element {
  const service = useMemo(() => getContainer().getAdminPersonalTokenService(), []);
  const [tokens, setTokens] = useState<AdminPersonalToken[]>([]);
  const [maxActiveTokens, setMaxActiveTokens] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  const [mintDialogOpen, setMintDialogOpen] = useState(false);
  const [readCount, setReadCount] = useState(0);

  /**
   * Revoking and minting each ask for the list again, so two reads can be in flight at once and the
   * slower one would otherwise answer last and put its older list back on screen. Asking for a read
   * by counting instead of calling the fetch directly routes every read through this one effect:
   * React tears the previous one down before starting the next, and the flag its cleanup sets is
   * what keeps a superseded read from writing anything.
   */
  useEffect(() => {
    let ignore = false;

    async function loadTokens(): Promise<void> {
      try {
        const response = await service.list();
        if (ignore) {
          return;
        }
        setTokens(response.tokens ?? []);
        setMaxActiveTokens(response.maxActiveTokens);
        setError(undefined);
      } catch (loadError) {
        if (ignore) {
          return;
        }
        setError(httpErrorMessage(loadError, t("api-tokens.load-failed", undefined, "Could not load your API tokens.")));
      }
    }

    void loadTokens();

    return () => {
      ignore = true;
    };
  }, [service, readCount]);

  const reload = useCallback((): void => {
    setReadCount((count) => count + 1);
  }, []);

  const activeTokens = tokens.filter((token) => token.status === TokenStatus.ACTIVE).length;
  // A response without the cap is one this card cannot read a limit from, and inventing one would
  // either block a mint the server allows or promise one it refuses.
  const capReached = maxActiveTokens !== undefined && activeTokens >= maxActiveTokens;

  async function revoke(tokenId: number): Promise<void> {
    await service.revoke(tokenId);
    reload();
  }

  return (
    <div
      className="card"
      data-testid="api-tokens-card"
    >
      <div className="card-body">
        <h5>{t("api-tokens.title", undefined, "API tokens")}</h5>
        <p className="text-muted">
          {t(
            "api-tokens.description",
            undefined,
            "An API token lets a desktop client act as you. It carries your own permissions, so treat one like your password.",
          )}
        </p>

        {error && (
          <div
            className="alert alert-danger"
            role="alert"
            data-testid="api-tokens-error"
          >
            {error}
          </div>
        )}

        <table
          className="table table-striped"
          data-testid="api-tokens-table"
        >
          <thead>
            <tr>
              <th>{t("api-tokens.column.label", undefined, "Label")}</th>
              <th>{t("api-tokens.column.created", undefined, "Created")}</th>
              <th>{t("api-tokens.column.expires", undefined, "Expires")}</th>
              <th>{t("api-tokens.column.status", undefined, "Status")}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {tokens.map((token) => (
              <TokenRow
                key={token.id}
                token={token}
                onRevoke={revoke}
              />
            ))}
          </tbody>
        </table>

        {tokens.length === 0 && !error && (
          <p
            className="text-muted"
            data-testid="api-tokens-empty"
          >
            {t("api-tokens.empty", undefined, "You have no API tokens yet.")}
          </p>
        )}

        {maxActiveTokens !== undefined && (
          <p
            className="text-muted"
            data-testid="api-tokens-active-count"
          >
            {t("api-tokens.active-count", { active: activeTokens, max: maxActiveTokens }, "{{active}} of {{max}} active API tokens in use.")}
          </p>
        )}

        {capReached && (
          <div
            className="alert alert-warning"
            data-testid="api-tokens-cap-reached"
          >
            {t(
              "api-tokens.cap-reached",
              { max: maxActiveTokens },
              "You hold the maximum of {{max}} active API tokens. Revoke one to free a slot before creating another.",
            )}
          </div>
        )}

        {/* Minting is a write on the server, so the control that starts one follows the same gate as revoking. */}
        <PermissionRequired
          permissions={[PermissionCategory.MCP_ACCESS]}
          accessLevel="w"
        >
          <button
            type="button"
            className="btn btn-primary waves-effect waves-light"
            data-testid="api-tokens-create"
            disabled={capReached}
            onClick={() => setMintDialogOpen(true)}
          >
            {t("api-tokens.create", undefined, "New API token")}
          </button>
        </PermissionRequired>

        {mintDialogOpen && (
          <MintTokenDialog
            mfaProvider={mfaProvider}
            onMinted={reload}
            onClose={() => setMintDialogOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
